import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { Button } from "@volca/ui/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@volca/ui/components/ui/hover-card";
import { Stack } from "@volca/ui/components/ui/stack";
import { useBreakpoints } from "@volca/ui/hooks/use-breakpoints";
import { cn } from "@volca/ui/lib/utils";
import type { ApReviewQueueItem } from "@/accounts-payable/types";
import { ApInvoiceSegmentPreview } from "./invoice-segment-preview";

const NAV_BTN = "rounded-full text-subtle";

export const getCarouselWindow = (
  total: number,
  currentIndex: number,
  maxVisible: number,
) => {
  if (total <= 0 || currentIndex < 0 || maxVisible <= 0) return [];

  const visibleCount = Math.min(total, maxVisible);
  const preferredStart = currentIndex - Math.floor(visibleCount / 2);
  const start = Math.max(0, Math.min(preferredStart, total - visibleCount));

  return Array.from({ length: visibleCount }, (_, offset) => start + offset);
};

export const getCarouselVisibleCount = ({
  isSm,
  isMd,
  isLg,
}: {
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
}) => {
  if (isSm) return 3;
  if (isMd) return 5;
  if (isLg) return 7;
  return 9;
};

export const getCarouselSegmentTier = (index: number, currentIndex: number) => {
  const distance = Math.abs(index - currentIndex);
  if (distance === 0) return "active" as const;
  if (distance === 1) return "near" as const;
  if (distance === 2) return "middle" as const;
  return "far" as const;
};

export const findFirstNewQueueItem = (
  pages: { items: ApReviewQueueItem[] }[] | undefined,
  loadedIds: string[],
) => {
  const loadedIdSet = new Set(loadedIds);
  return pages
    ?.flatMap((page) => page.items)
    .find((item) => !loadedIdSet.has(item.id));
};

export const getCarouselKeyboardDirection = (
  key: string,
  target: EventTarget | null,
) => {
  const element = target instanceof HTMLElement ? target : null;
  if (
    element &&
    (element.closest(
      'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]',
    ) ||
      element.isContentEditable)
  ) {
    return null;
  }
  /**
   * An open modal owns the keyboard. The listener is on `window`, so arrow
   * keys reach it even from inside a dialog — and stepping to another invoice
   * behind one is worse than a no-op: the manual job-match form keeps its
   * selected job while `invoiceId` changes underneath it, so confirming would
   * attribute that job to a different invoice. The overlay and other portalled
   * furniture sit outside `[role="dialog"]`, hence the document-level check
   * rather than relying on the target alone.
   */
  const doc = element?.ownerDocument ?? globalThis.document;
  if (doc?.querySelector('[role="dialog"][data-open]')) return null;
  if (key === "ArrowLeft") return "previous" as const;
  if (key === "ArrowRight") return "next" as const;
  return null;
};

export const ReviewQueueCarousel = ({
  items,
  currentIndex,
  hasNextPage,
  isFetchingNextPage,
  onSelect,
  onPrevious,
  onNext,
}: {
  items: ApReviewQueueItem[];
  currentIndex: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onSelect: (id: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}) => {
  const { isSm, isMd, isLg } = useBreakpoints();
  const visibleCount = getCarouselVisibleCount({ isSm, isMd, isLg });
  const visibleIndices = getCarouselWindow(
    items.length,
    currentIndex,
    visibleCount,
  );
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < items.length - 1 || hasNextPage;

  return (
    <Stack
      direction="row"
      gap={2}
      align="center"
      justify="center"
      className="max-w-full"
    >
      <Button
        variant="outline"
        size="icon"
        aria-label="Previous invoice"
        disabled={!canGoPrevious}
        onClick={onPrevious}
        className={NAV_BTN}
      >
        <ArrowLeftIcon className="h-4 w-4" />
      </Button>
      <Stack
        direction="row"
        gap={2}
        align="center"
        role="group"
        aria-label="Invoice review queue"
        className="min-w-0"
      >
        {visibleIndices.map((index) => {
          const item = items[index];
          const isCurrent = index === currentIndex;
          const tier = getCarouselSegmentTier(index, currentIndex);

          return (
            <HoverCard key={item.id}>
              <HoverCardTrigger
                delay={150}
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Go to ${item.vendor_name} (${index + 1} of ${items.length})`}
                    aria-current={isCurrent}
                    onClick={() => onSelect(item.id)}
                    className={cn("group rounded-full hover:bg-transparent", {
                      "w-10": tier === "active",
                      "w-8": tier === "near",
                      "w-6": tier === "middle",
                      "w-4": tier === "far",
                    })}
                  />
                }
              >
                <span
                  className={cn(
                    "w-full rounded-full bg-line transition-colors group-hover:bg-neutral-subtle-hovered",
                    {
                      "h-2.5 bg-brand-bold opacity-100 shadow-xs group-hover:bg-brand-bold-hover":
                        tier === "active",
                      "h-2 opacity-80": tier === "near",
                      "h-1.5 opacity-60": tier === "middle",
                      "h-1 opacity-40": tier === "far",
                    },
                  )}
                />
              </HoverCardTrigger>
              {/* `w-auto` lets the 320px preview set the width; `max-w-md`
                    leaves room for its padding, which `md` used to clip. */}
              <HoverCardContent side="top" className="w-auto max-w-md p-4">
                <ApInvoiceSegmentPreview
                  invoice={item}
                  actions={item.last_completed_agent_run.actions}
                />
              </HoverCardContent>
            </HoverCard>
          );
        })}
      </Stack>
      <Button
        variant="outline"
        size="icon"
        aria-label="Next invoice"
        disabled={!canGoNext || isFetchingNextPage}
        onClick={onNext}
        className={NAV_BTN}
      >
        <ArrowRightIcon className="h-4 w-4" />
      </Button>
      <span className="ml-1 flex-none text-xs font-medium whitespace-nowrap text-subtle">
        {currentIndex + 1} / {items.length}
        {hasNextPage ? "+" : ""}
      </span>
    </Stack>
  );
};
